import {
  createSelector,
  createSlice,
  nanoid,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AITaskResponseResultData } from 'api/collaborateApi';
import { CollaborateTaskData } from 'types/api/collaborate';
import { Comment, TaskAttributes } from 'types/api/tasks';
import { RootState } from '../store';

export type PendingTask = {
  task_comment_id: number;
  task_id: string;
  task_status: TaskStatus;
  task_priority: TaskPriority;
  is_internal: boolean;
  task_element_path: string;
  screenshot?: string;
};

interface TasksState {
  tasks: (CollaborateTaskData | PendingTask)[];
  createModalOpen: boolean | string | number;
  creatingTask: boolean;
  animateScreenshot: boolean;
  mostRecentCommentId: number;
  annotateModalOpen: boolean;
  annotateImage: string;
  selectedPage: string | null;
  selectedTaskId: string | null;
  lostTasks: string[];
  notifyUsers: string[];
  pageId?: string;
  isDiscardModalOpen?: boolean;
  claroPending: Record<string, boolean>;
  aiPreviewImg: string;
  isLoadingAi: boolean;
  isAIActive: boolean;
  loadingAiComments: Record<string, string>;
  selectedTask?: Partial<TaskAttributes>;
  selectedAiTask?: Partial<AITaskResponseResultData>;
  latestTaskCommentId?: number;
  taskOffset: number;
  stickerAnimationState: Record<string, boolean>;
  shouldAnimateComments: boolean;
  shouldShowSkeleton: boolean;
  isOpeningNewTask: boolean;
  generalTaskModalOpen: boolean | string;
  loadingAiDesignReview: Record<string, string>;
  // Track which sticker hover tooltip is open globally to ensure only one at a time
  currentHoverStickerId: string | null;
}

export interface NotifyuserProps {
  value: string | number;
  label: string | undefined;
  email: string;
  avatar: string;
  role: string;
  checked: boolean;
  responsible: boolean;
  normal_id?: number | string;
}

export type TaskStatus = 'open' | 'in-progress' | 'pending-review' | 'complete';
export type TaskPriority = 'low' | 'critical' | 'high' | 'medium';

const initialState: TasksState = {
  tasks: [],
  createModalOpen: false,
  creatingTask: false,
  animateScreenshot: false,
  mostRecentCommentId: 0,
  annotateModalOpen: false,
  annotateImage: '',
  selectedPage: null,
  selectedTaskId: null,
  lostTasks: [],
  notifyUsers: [],
  pageId: '',
  isDiscardModalOpen: false,
  claroPending: {},
  aiPreviewImg: '',
  isLoadingAi: false,
  isAIActive: false,
  loadingAiComments: {},
  selectedTask: undefined,
  selectedAiTask: undefined,
  latestTaskCommentId: undefined,
  taskOffset: 0,
  shouldAnimateComments: false,
  shouldShowSkeleton: false,
  isOpeningNewTask: true,
  generalTaskModalOpen: false,
  stickerAnimationState: {},
  loadingAiDesignReview: {},
  currentHoverStickerId: null,
};

// Helper function to ensure a PENDING task exists
const ensurePendingTask = (state: TasksState): PendingTask => {
  let pendingTask = state.tasks.find((t) =>
    t.task_id.includes('PENDING')
  ) as PendingTask;

  if (!pendingTask) {
    // Create a default PENDING task if none exists
    pendingTask = {
      task_comment_id: state.tasks.length + 1,
      task_id: `PENDING_${nanoid(6)}`,
      task_status: 'open',
      task_priority: 'low',
      is_internal: false,
      task_element_path: '',
    };
    state.tasks.unshift(pendingTask);
  }

  return pendingTask;
};

const tasksSlice = createSlice({
  initialState,
  name: 'tasks',
  reducers: {
    setTasks: (
      state,
      action: PayloadAction<(CollaborateTaskData | PendingTask)[]>
    ) => {
      state.tasks = action.payload;
      state.latestTaskCommentId =
        Math.max(...action.payload.map((t) => t.task_comment_id)) + 1 || 0;
    },
    addTask: (
      state,
      action: PayloadAction<CollaborateTaskData | PendingTask>
    ) => {
      state.tasks.unshift(action.payload);
    },
    clearPendingTask: (state) => {
      state.tasks = state.tasks.filter((t) => !t.task_id.includes('PENDING'));
    },
    creatingTask: (state, action: PayloadAction<boolean>) => {
      state.creatingTask = action.payload;
    },
    setAnimateScreenshot: (state, action: PayloadAction<boolean>) => {
      state.animateScreenshot = action.payload;
    },
    setCreateModalOpen: (
      state,
      action: PayloadAction<boolean | string | number>
    ) => {
      state.createModalOpen = action.payload;
      // If createModalOpen is being set to a truthy value, close generalTaskModalOpen
      if (action.payload) {
        state.generalTaskModalOpen = false;
      }
    },
    setMostRecentCommentId: (state, action: PayloadAction<number>) => {
      state.mostRecentCommentId = action.payload;
    },
    replaceTask: (
      state,
      action: PayloadAction<{
        task_id: string;
        task: CollaborateTaskData | PendingTask;
      }>
    ) => {
      // Find and replace either PENDING or OPTIMISTIC tasks
      const taskIndex = state.tasks.findIndex((t) =>
        t.task_id.includes(action.payload.task_id)
      );
      if (taskIndex !== -1) {
        state.tasks[taskIndex] = action.payload.task;
      }
    },
    setPendingTask: (state, action: PayloadAction<PendingTask>) => {
      const task = state.tasks.find((t) =>
        t.task_id.includes('PENDING')
      ) as PendingTask;
      if (task) {
        Object.assign(task, action.payload);
      } else {
        state.tasks.unshift({
          ...action.payload,
        });
      }
    },
    setPendingTaskPriority: (state, action: PayloadAction<TaskPriority>) => {
      const task = ensurePendingTask(state);
      task.task_priority = action.payload;
    },
    addCommentToTask: (
      state,
      action: PayloadAction<{ task_id: string; comment: Comment }>
    ) => {
      const task = state.selectedTask;
      if (task && task.comments && task.task_id === action.payload.task_id) {
        const commentIndex = task.comments?.findIndex(
          (c) => c.id === action.payload.comment.id
        );

        if (commentIndex !== -1) {
          task.comments[commentIndex] = action.payload.comment;
        } else {
          task.comments?.push(action.payload.comment);
        }

        // Ensure skeleton is hidden when adding optimistic comments
        if (action.payload.comment.id.includes('OPTIMISTIC')) {
          state.shouldShowSkeleton = false;
          state.shouldAnimateComments = true;
        }
      }
    },
    replaceComment: (
      state,
      action: PayloadAction<{
        task_id: string;
        comment: Comment;
        comment_id: string;
      }>
    ) => {
      const task = state.selectedTask;
      if (task && task.comments && task.task_id === action.payload.task_id) {
        const commentIndex = task.comments?.findIndex(
          (c) => c.id === action.payload.comment_id
        );
        if (commentIndex !== -1) {
          task.comments[commentIndex] = action.payload.comment;
          state.isOpeningNewTask = false;
        }
      }
    },
    removeCommentFromTask: (
      state,
      action: PayloadAction<{ task_id: string; comment_id: string }>
    ) => {
      const task = state.selectedTask;
      if (task && task.comments && task.task_id === action.payload.task_id) {
        task.comments = task.comments.filter(
          (c) => c.id !== action.payload.comment_id
        );
      }
    },
    setPendingTaskStatus: (state, action: PayloadAction<TaskStatus>) => {
      const task = ensurePendingTask(state);
      task.task_status = action.payload;
    },
    setPendingTaskInternal: (state, action: PayloadAction<boolean>) => {
      const task = ensurePendingTask(state);
      task.is_internal = action.payload;
    },
    setSelectedTaskId: (state, action: PayloadAction<string | null>) => {
      const selectedTaskId = state.selectedTaskId;
      if (
        action.payload?.includes('OPTIMISTIC') ||
        selectedTaskId?.includes('OPTIMISTIC') ||
        action.payload?.includes('PENDING') ||
        selectedTaskId?.includes('PENDING')
      ) {
        state.shouldShowSkeleton = false;
        state.shouldAnimateComments = true;
        state.selectedTaskId = action.payload;
        return;
      }
      state.shouldShowSkeleton = true;
      state.isAIActive = false;
      if (action.payload === null) {
        state.selectedTaskId = null;
        return;
      } else {
        state.selectedTaskId = action.payload;
      }
    },
    updatePendingTaskScreenshot: (
      state,
      action: PayloadAction<{ taskId: string; screenshot: string }>
    ) => {
      const task = ensurePendingTask(state);
      task.screenshot = action.payload.screenshot;
    },
    setAnnotateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.annotateModalOpen = action.payload;
    },
    setAnnotateImage: (state, action: PayloadAction<string>) => {
      state.annotateImage = action.payload;
    },
    addLostTask: (state, action: PayloadAction<string>) => {
      if (!state.lostTasks.includes(action.payload)) {
        state.lostTasks.push(action.payload);
      }
    },
    removeLostTask: (state, action: PayloadAction<string>) => {
      state.lostTasks = state.lostTasks.filter((id) => id !== action.payload);
    },
    clearLostTasks: (state) => {
      state.lostTasks = [];
    },
    setNotifiedUsers: (state, action: PayloadAction<string[]>) => {
      state.notifyUsers = action.payload;
    },
    setPageId: (state, action: PayloadAction<string>) => {
      state.pageId = action.payload;
    },
    setIsDiscardModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isDiscardModalOpen = action.payload;
    },
    setClaroPending: (
      state,
      action: PayloadAction<{ task_id: string; pending: boolean }>
    ) => {
      const { task_id, pending } = action.payload;
      state.claroPending[task_id] = pending;
    },
    setAIPreviewImg: (state, action: PayloadAction<string>) => {
      state.aiPreviewImg = action.payload;
    },
    setIsLoadingAi: (state, action: PayloadAction<boolean>) => {
      state.isLoadingAi = action.payload;
    },
    setIsAIActive: (state, action: PayloadAction<boolean>) => {
      state.isAIActive = action.payload;
    },
    setIsLoadingAIComment: (
      state,
      action: PayloadAction<{
        task_id: string;
        status: 'running' | 'completed' | 'pending' | 'failed';
      }>
    ) => {
      state.loadingAiComments[action.payload.task_id] = action.payload.status;
    },
    removeIsLoadingAIComment: (state, action: PayloadAction<string>) => {
      delete state.loadingAiComments[action.payload];
    },
    setSelectedTask: (
      state,
      action: PayloadAction<Partial<TaskAttributes> | undefined>
    ) => {
      const currentTask = state.selectedTask;

      if (action.payload?.task_id) {
        const taskIndex = state.tasks.findIndex(
          (t) => t.task_id === action.payload?.task_id
        );
        if (taskIndex !== -1 && 'is_unread' in state.tasks[taskIndex]) {
          state.tasks[taskIndex].is_unread = false;
        }
      }

      state.shouldShowSkeleton = false;
      if (!action.payload) {
        // its a pending task
        state.shouldAnimateComments = true;
        state.isOpeningNewTask = false;
        state.selectedTask = action.payload;
      } else if (currentTask?.task_id === action.payload?.task_id) {
        // same task - preserve optimistic comments
        const optimisticComments =
          currentTask?.comments?.filter((comment) =>
            comment.id.includes('OPTIMISTIC')
          ) || [];

        const serverComments = action.payload?.comments || [];

        // Merge optimistic comments with server comments, avoiding duplicates
        const mergedComments = [...serverComments];
        optimisticComments.forEach((optimisticComment) => {
          const exists = mergedComments.some(
            (comment) => comment.id === optimisticComment.id
          );
          if (!exists) {
            mergedComments.push(optimisticComment);
          }
        });

        // Update the task with merged comments
        state.selectedTask = {
          ...action.payload,
          comments: mergedComments,
        };

        // Set animation state based on whether we have optimistic comments
        if (optimisticComments.length > 0) {
          state.shouldAnimateComments = true;
        } else {
          state.shouldAnimateComments = false;
        }
        state.isOpeningNewTask = false;
      } else if (
        currentTask?.task_id?.includes('OPTIMISTIC') &&
        !action.payload?.task_id?.includes('OPTIMISTIC')
      ) {
        // going from optimistic to normal
        state.shouldAnimateComments = false;
        state.isOpeningNewTask = false;
        state.selectedTask = action.payload;
      } else if (!currentTask && action.payload) {
        // its an optimistic task
        if (action.payload?.task_id?.includes('OPTIMISTIC')) {
          state.shouldAnimateComments = true;
          state.isOpeningNewTask = false;
        } else {
          // its an initial task load
          state.shouldAnimateComments = true;
          state.isOpeningNewTask = true;
        }
        state.selectedTask = action.payload;
      } else {
        // opening a new existing task
        state.shouldAnimateComments = true;
        state.isOpeningNewTask = true;
        state.selectedTask = action.payload;
      }
    },
    setLatestTaskCommentId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.latestTaskCommentId = action.payload;
    },
    setTaskOffset: (state, action: PayloadAction<number>) => {
      state.taskOffset = action.payload;
    },
    setShouldAnimateComments: (state, action: PayloadAction<boolean>) => {
      state.shouldAnimateComments = action.payload;
    },
    setShouldShowSkeleton: (state, action: PayloadAction<boolean>) => {
      state.shouldShowSkeleton = action.payload;
    },
    setIsOpeningNewTask: (state, action: PayloadAction<boolean>) => {
      state.isOpeningNewTask = action.payload;
    },
    setGeneralTaskModalOpen: (
      state,
      action: PayloadAction<boolean | string>
    ) => {
      state.generalTaskModalOpen = action.payload;
      // If generalTaskModalOpen is being set to a truthy value, close createModalOpen
      if (action.payload) {
        state.createModalOpen = false;
      }
    },
    setCurrentHoverStickerId: (state, action: PayloadAction<string | null>) => {
      state.currentHoverStickerId = action.payload;
    },
    setStickerAnimationState: (
      state,
      action: PayloadAction<{ comment_id: number; state: boolean }>
    ) => {
      state.stickerAnimationState[action.payload.comment_id] =
        action.payload.state;
    },
    removeStickerAnimationState: (state, action: PayloadAction<number>) => {
      delete state.stickerAnimationState[action.payload];
    },
    setIsLoadingAIDesignReview: (
      state,
      action: PayloadAction<{
        page_id: string;
        status: 'running' | 'completed' | 'progressed' | 'pending' | 'failed';
      }>
    ) => {
      state.loadingAiDesignReview[action.payload.page_id] =
        action.payload.status;
    },
    removeIsLoadingAIDesignReview: (state, action: PayloadAction<string>) => {
      delete state.loadingAiDesignReview[action.payload];
    },
  },
});

export const {
  addTask,
  setTasks,
  clearPendingTask,
  creatingTask,
  setCreateModalOpen,
  setMostRecentCommentId,
  setPendingTask,
  setPendingTaskPriority,
  setPendingTaskStatus,
  setPendingTaskInternal,
  setSelectedTaskId,
  updatePendingTaskScreenshot,
  setAnimateScreenshot,
  setAnnotateModalOpen,
  setAnnotateImage,
  addLostTask,
  removeLostTask,
  clearLostTasks,
  setNotifiedUsers,
  setPageId,
  setIsDiscardModalOpen,
  setClaroPending,
  setAIPreviewImg,
  setIsLoadingAi,
  removeIsLoadingAIComment,
  setIsAIActive,
  setIsLoadingAIComment,
  setSelectedTask,
  setLatestTaskCommentId,
  setTaskOffset,
  replaceTask,
  setShouldAnimateComments,
  setShouldShowSkeleton,
  setIsOpeningNewTask,
  addCommentToTask,
  replaceComment,
  setGeneralTaskModalOpen,
  setCurrentHoverStickerId,
  removeCommentFromTask,
  setStickerAnimationState,
  removeStickerAnimationState,
  setIsLoadingAIDesignReview,
  removeIsLoadingAIDesignReview,
} = tasksSlice.actions;

// Basic selectors - these don't need memoization as they return primitive values directly
export const getTasks = (state: RootState) => state.tasks.tasks;
export const getAnimateScreenshot = (state: RootState) =>
  state.tasks.animateScreenshot;
export const getCreatingTask = (state: RootState) => state.tasks.creatingTask;
export const getCreateModalOpen = (state: RootState) =>
  state.tasks.createModalOpen;
export const getSelectedTaskId = (state: RootState) =>
  state.tasks.selectedTaskId;
export const getAnnotateModalOpen = (state: RootState) =>
  state.tasks.annotateModalOpen;
export const getAnnotateImage = (state: RootState) => state.tasks.annotateImage;
export const getLostTasks = (state: RootState) => state.tasks.lostTasks;
export const getNotifiedUsers = (state: RootState) => state.tasks.notifyUsers;
export const getPageId = (state: RootState) => state.tasks.pageId;
export const getIsDiscardModalOpen = (state: RootState) =>
  state.tasks.isDiscardModalOpen;
export const getClaroPending = (state: RootState) => state.tasks.claroPending;
export const getAIPreviewImg = (state: RootState) => state.tasks.aiPreviewImg;
export const getIsLoadingAi = (state: RootState) => state.tasks.isLoadingAi;
export const getIsAIActive = (state: RootState) => state.tasks.isAIActive;
export const getLoadingAIComments = (state: RootState) =>
  state.tasks.loadingAiComments;
export const getSelectedTask = (state: RootState) => state.tasks.selectedTask;
export const getLatestTaskCommentId = (state: RootState) =>
  state.tasks.latestTaskCommentId;
export const getTaskOffset = (state: RootState) => state.tasks.taskOffset;
export const getShouldAnimateComments = (state: RootState) =>
  state.tasks.shouldAnimateComments;
export const getShouldShowSkeleton = (state: RootState) =>
  state.tasks.shouldShowSkeleton;
export const getIsOpeningNewTask = (state: RootState) =>
  state.tasks.isOpeningNewTask;
export const getGeneralTaskModalOpen = (state: RootState) =>
  state.tasks.generalTaskModalOpen;
export const getStickerAnimationState = (state: RootState) =>
  state.tasks.stickerAnimationState;
export const getLoadingAiDesignReview = (state: RootState) =>
  state.tasks.loadingAiDesignReview;
export const getCurrentHoverStickerId = (state: RootState) =>
  state.tasks.currentHoverStickerId;

// Memoized selectors for computed/filtered data

export const getMostRecentCommentId = createSelector([getTasks], (tasks) => {
  return tasks.reduce((max, task) => {
    return Math.max(max, task.task_comment_id || 0);
  }, 0);
});

export const getPendingTask = createSelector([getTasks], (tasks) => {
  return tasks.find((t) => t.task_id.includes('PENDING')) as
    | PendingTask
    | undefined;
});

export const getPendingTaskStatus = createSelector(
  [getPendingTask],
  (pendingTask) => {
    return (pendingTask?.task_status as TaskStatus) ?? 'open';
  }
);
export const getPendingTaskPriority = createSelector(
  [getPendingTask],
  (pendingTask) => {
    return (pendingTask?.task_priority as TaskPriority) ?? 'low';
  }
);

export const getRealTasks = createSelector([getTasks], (tasks) => {
  return tasks.filter(
    (t) => !t.task_id.includes('PENDING') && !t.task_id.includes('OPTIMISTIC')
  );
});

export const getOptimisticTask = createSelector([getTasks], (tasks) => {
  return tasks.find((t) => t.task_id.includes('OPTIMISTIC')) as
    | CollaborateTaskData
    | undefined;
});

// Selectors for task counts and statistics
export const getTaskCounts = createSelector([getTasks], (tasks) => {
  const real = tasks.filter(
    (t) => !t.task_id.includes('PENDING') && !t.task_id.includes('OPTIMISTIC')
  );
  const pending = tasks.filter((t) => t.task_id.includes('PENDING'));
  const optimistic = tasks.filter((t) => t.task_id.includes('OPTIMISTIC'));

  return {
    total: tasks.length,
    real: real.length,
    pending: pending.length,
    optimistic: optimistic.length,
  };
});

// Task status breakdown
export const getTasksByStatus = createSelector([getRealTasks], (realTasks) => {
  const grouped = realTasks.reduce(
    (acc, task) => {
      const status = 'task_status' in task ? task.task_status : 'unknown';
      if (!acc[status]) acc[status] = [];
      acc[status].push(task);
      return acc;
    },
    {} as Record<string, (CollaborateTaskData | PendingTask)[]>
  );

  return {
    ...grouped,
    counts: {
      open: grouped.open?.length || 0,
      'in-progress': grouped['in-progress']?.length || 0,
      'pending-review': grouped['pending-review']?.length || 0,
      complete: grouped.complete?.length || 0,
    },
  };
});

// Task priority breakdown
export const getTasksByPriority = createSelector(
  [getRealTasks],
  (realTasks) => {
    const grouped = realTasks.reduce(
      (acc, task) => {
        const priority =
          'task_priority' in task ? task.task_priority : 'medium';
        if (!acc[priority]) acc[priority] = [];
        acc[priority].push(task);
        return acc;
      },
      {} as Record<string, (CollaborateTaskData | PendingTask)[]>
    );

    return {
      ...grouped,
      counts: {
        critical: grouped.critical?.length || 0,
        high: grouped.high?.length || 0,
        medium: grouped.medium?.length || 0,
        low: grouped.low?.length || 0,
      },
    };
  }
);

// AI-related state
export const getAIState = createSelector(
  [getIsLoadingAi, getIsAIActive, getLoadingAIComments, getAIPreviewImg],
  (isLoadingAi, isAIActive, loadingAiComments, aiPreviewImg) => ({
    isLoadingAi,
    isAIActive,
    loadingAiComments,
    aiPreviewImg,
    hasAIPreview: !!aiPreviewImg,
    isAIBusy:
      isLoadingAi ||
      Object.values(loadingAiComments).some(
        (status) => status === 'running' || status === 'pending'
      ),
  })
);

// Modal state
export const getModalState = createSelector(
  [getCreateModalOpen, getAnnotateModalOpen, getIsDiscardModalOpen],
  (createModalOpen, annotateModalOpen, isDiscardModalOpen) => ({
    createModalOpen,
    annotateModalOpen,
    isDiscardModalOpen,
    anyModalOpen:
      !!createModalOpen || annotateModalOpen || !!isDiscardModalOpen,
  })
);

// UI animation state
export const getUIAnimationState = createSelector(
  [
    getShouldAnimateComments,
    getShouldShowSkeleton,
    getIsOpeningNewTask,
    getAnimateScreenshot,
  ],
  (
    shouldAnimateComments,
    shouldShowSkeleton,
    isOpeningNewTask,
    animateScreenshot
  ) => ({
    shouldAnimateComments,
    shouldShowSkeleton,
    isOpeningNewTask,
    animateScreenshot,
    isAnimating:
      shouldAnimateComments ||
      shouldShowSkeleton ||
      isOpeningNewTask ||
      animateScreenshot,
  })
);

// Current task state - useful for task detail components
export const getCurrentTaskState = createSelector(
  [getSelectedTask, getSelectedTaskId, getPendingTask, getOptimisticTask],
  (selectedTask, selectedTaskId, pendingTask, optimisticTask) => {
    const isPending = selectedTaskId?.includes('PENDING');
    const isOptimistic = selectedTaskId?.includes('OPTIMISTIC');

    return {
      selectedTask,
      selectedTaskId,
      pendingTask,
      optimisticTask,
      isPending,
      isOptimistic,
      hasSelectedTask: !!selectedTask || !!pendingTask || !!optimisticTask,
      currentTask: isPending
        ? pendingTask
        : isOptimistic
          ? optimisticTask
          : selectedTask,
    };
  }
);

export default tasksSlice.reducer;
